import classNames from "classnames";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import {
  browserTimeZone,
  formatSendTime,
  fromLocalInput,
  quickPicks,
  scheduleProblem,
  toLocalInput,
} from "./schedule.utils";
import { PRIMARY_BUTTON, SMALL_BUTTON } from "./supportUi";

export const ClockIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox={"0 0 16 16"}
    width={14}
    height={14}
    aria-hidden={"true"}
    className={className}
    fill={"none"}
    stroke={"currentColor"}
    strokeWidth={1.6}
    strokeLinecap={"round"}>
    <circle cx={8} cy={8} r={6.2} />
    <path d={"M8 4.6V8l2.4 1.6"} />
  </svg>
);

const MENU_WIDTH = 330;

/**
 * "Schedule send" (owner 2026-11-03): a button with a small menu of quick times (this afternoon, tomorrow 8 AM,
 * Monday 8 AM) and a date-and-time box, in the browser's time zone. The menu is drawn at the page root so a
 * window's scrolling body or footer never cuts it off; it opens upward when there's no room below.
 */
export const ScheduleSendButton = ({
  onSchedule,
  disabled,
  busy,
  title,
  label = "Schedule send",
  initial,
  testId = "schedule-send",
}: {
  onSchedule: (at: Date) => void;
  disabled?: boolean;
  busy?: boolean;
  title?: string;
  label?: string;
  /** The date-and-time box starts here (Edit time: the current time). */
  initial?: Date;
  testId?: string;
}) => {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState("");
  const [pos, setPos] = useState<{
    left: number;
    top?: number;
    bottom?: number;
  }>({ left: 0 });
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const now = new Date();
  const picks = open ? quickPicks(now) : [];
  const chosen = fromLocalInput(custom);
  const problem = custom ? scheduleProblem(chosen, now) : "";
  const zone = browserTimeZone();

  useLayoutEffect(() => {
    if (!open || !button.current) return;
    const r = button.current.getBoundingClientRect();
    const left = Math.max(
      8,
      Math.min(r.left, window.innerWidth - MENU_WIDTH - 8),
    );
    const below = window.innerHeight - r.bottom;
    setPos(
      below > 330 || below > r.top
        ? { left, top: r.bottom + 6 }
        : { left, bottom: window.innerHeight - r.top + 6 },
    );
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setCustom(toLocalInput(initial || quickPicks(new Date())[0].at));
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!menu.current?.contains(t) && !button.current?.contains(t))
        setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pick = (at: Date) => {
    setOpen(false);
    onSchedule(at);
  };

  return (
    <>
      <button
        ref={button}
        type={"button"}
        disabled={disabled || busy}
        title={title}
        aria-haspopup={"menu"}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={classNames(
          SMALL_BUTTON,
          "inline-flex h-10 items-center gap-1.5",
        )}
        data-testid={testId}>
        <ClockIcon />
        {busy ? "Scheduling…" : label}
        <span aria-hidden={"true"} className={"text-[10px] text-[#7A7F8C]"}>
          ▾
        </span>
      </button>
      {open &&
        createPortal(
          <div
            ref={menu}
            role={"menu"}
            aria-label={label}
            data-testid={"schedule-menu"}
            className={
              "fixed z-[1100] max-h-[80vh] overflow-y-auto rounded-xl border border-[#DCE2EA] bg-white p-2 text-[#1F2433] shadow-[0_14px_40px_rgba(31,36,51,0.18)]"
            }
            style={{ width: MENU_WIDTH, ...pos }}>
            <div
              className={
                "px-2 pb-1 pt-1 text-[11.5px] font-bold uppercase tracking-wide text-[#7A7F8C]"
              }>
              Send it
            </div>
            {picks.map((p) => (
              <button
                key={p.key}
                type={"button"}
                role={"menuitem"}
                onClick={() => pick(p.at)}
                className={
                  "flex w-full items-center justify-between gap-3 rounded-lg px-2.5 py-2 text-left text-[13.5px] hover:bg-[#F4F1FF]"
                }>
                <span className={"whitespace-nowrap font-semibold"}>
                  {p.label}
                </span>
                <span
                  className={"whitespace-nowrap text-[12.5px] text-[#5D6475]"}>
                  {p.detail}
                </span>
              </button>
            ))}
            <div className={"mx-1 my-2 border-t border-[#EDEFF3]"} />
            <label
              htmlFor={`${testId}-custom`}
              className={"block px-2 text-[12.5px] font-bold text-[#3A4256]"}>
              Pick a date and time
            </label>
            <div className={"flex items-center gap-2 px-2 pt-1.5"}>
              <input
                id={`${testId}-custom`}
                type={"datetime-local"}
                value={custom}
                min={toLocalInput(now)}
                onChange={(e) => setCustom(e.target.value)}
                className={
                  "h-9 min-w-0 flex-1 rounded-lg border border-[#DCE2EA] px-2 text-[13px] outline-none focus:border-[#6B4EFF]"
                }
              />
              <button
                type={"button"}
                disabled={!chosen || !!problem}
                onClick={() => chosen && pick(chosen)}
                className={classNames(PRIMARY_BUTTON, "shrink-0")}>
                Schedule
              </button>
            </div>
            <div
              className={classNames(
                "px-2 pb-1 pt-1.5 text-[12px]",
                problem ? "text-[#B45309]" : "text-[#7A7F8C]",
              )}>
              {problem ||
                (chosen ? `Goes out ${formatSendTime(chosen, now)}` : "") +
                  (zone ? ` · your time (${zone.replace(/_/g, " ")})` : "")}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};
